import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Link, Drawer, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, Menu as MenuIcon } from '@mui/icons-material';

import { FHTooltip } from '../../base';
import { areUserRolesIn } from '../../../helpers';
import { useSidebar } from '../../../contexts';
import { FORTA_BLUE, FORTA_WHITE } from '../../../utils/colors';
import NavItem, { itemStyle } from './NavItem';
import NestedNavItem from './NestedNavItem';
import { Roles, SIDEBAR_WIDTH } from '../../../constants';
import { usePreClinicalGuardianGating } from '../../../hooks';
import HomeIcon from '../../../assets/images/home-new.svg';
import NewIcon from '../../../assets/images/new.svg';
import CreateInvoiceIcon from '../../../assets/images/create-invoice-icon.svg';
import TaskIcon from '../../../assets/images/task.svg';
import HistoryIcon from '../../../assets/images/history.svg';
import AlarmIcon from '../../../assets/images/alarm.svg';
import HeartIcon from '../../../assets/images/heart.svg';
import ChartIcon from '../../../assets/images/chart.svg';
import ReportsIcon from '../../../assets/images/reports.svg';
import KnowledgeBaseIcon from '../../../assets/images/knowledge-base.svg';
import ClientIcon from '../../../assets/images/client.svg';
import DirectSession from '../../../assets/images/DirectSession.svg';
import ListIcon from '../../../assets/images/list-icon.svg';
import CalenderIcon from '../../../assets/images/calendar-icon.svg';
import SupervisionMetricsIcon from '../../../assets/images/supervision-metrics.svg';
import BtIcon from '../../../assets/images/bt.svg';
import ClientAssessmentIcon from '../../../assets/images/client-assessment.svg';
import GroupsIcon from '../../../assets/images/groups.svg';
import ParentsIcon from '../../../assets/images/parents.svg';
import EditSessionIcon from '../../../assets/images/edit-session.svg';
import CloseIcon from '../../svgs/CloseIcon';
import BillIcon from '../../../assets/images/bill.svg';
import BackgroundIcon from '../../../assets/images/history-new.svg';
import LogoIcon from '../../svgs/LogoIcon';
import LogoWithText from '../../../assets/images/logo.svg';
import AddFacilityIcon from '../../../assets/images/add-facility.svg';
import AddPersonIcon from '../../../assets/images/add-person.svg';
import AddCohortIcon from '../../../assets/images/add-cohort.svg';
import AddGoalTemplateIcon from '../../../assets/images/add-goal-template.svg';
import ChangeIcon from '../../../assets/images/change.svg';
import ReassignIcon from '../../../assets/images/swap.svg';
import DeleteIcon from '../../../assets/images/delete-outlined.svg';
import SendIcon from '../../../assets/images/send.svg';
import PaymentIcon from '../../../assets/images/payment.svg';

import type { TFunction } from 'i18next';
import type { RootState } from '../../../store';

export const nestedAdminActionsItems = [
  {
    title: 'Admin Home',
    to: '/admin',
    icon: HomeIcon,
    roles: [Roles.Admin, Roles.OnboardingCoordinator, Roles.Biller, Roles.ClientSuccessManager],
  },
  {
    title: 'Add Client',
    to: '/admin-add-client',
    icon: AddPersonIcon,
    roles: [Roles.Admin, Roles.OnboardingCoordinator],
  },
  {
    title: 'Add Cohort',
    to: '/admin/add-cohort',
    icon: AddCohortIcon,
    roles: [Roles.Admin],
  },
  {
    title: 'Add Facility',
    to: '/admin/add-billing-facility',
    icon: AddFacilityIcon,
    roles: [Roles.Admin, Roles.OnboardingCoordinator],
  },
  {
    title: 'Add Goal Template',
    to: '/admin/add-goal-template',
    icon: AddGoalTemplateIcon,
    roles: [Roles.Admin],
  },
  {
    title: 'Add Payor',
    to: '/admin-add-payor',
    icon: AddPersonIcon,
    roles: [Roles.Admin, Roles.OnboardingCoordinator],
  },
  {
    title: 'Add Provider',
    to: '/admin-add-provider',
    icon: AddPersonIcon,
    roles: [Roles.Admin, Roles.OnboardingCoordinator],
  },
  {
    title: 'Change Session Note Status',
    to: '/admin/change-session-status',
    icon: ChangeIcon,
    roles: [Roles.Admin],
  },
  {
    title: 'Change billing status',
    to: '/admin/change-billing-status',
    icon: ChangeIcon,
    roles: [Roles.Admin, Roles.OnboardingCoordinator],
  },
  {
    title: 'Change Claim Status',
    to: '/admin/change-claim-status',
    icon: ChangeIcon,
    roles: [Roles.Admin],
  },
  {
    title: 'Change Provider Email',
    to: '/admin/change-provider-email',
    icon: ChangeIcon,
    roles: [Roles.Admin],
  },
  {
    title: 'Delete Claim',
    to: '/admin/delete-claim',
    icon: DeleteIcon,
    roles: [Roles.Admin],
  },
  {
    title: 'Field templates',
    to: '/admin/field-templates',
    icon: ListIcon,
    roles: [Roles.Admin],
  },
  {
    title: 'EVV History',
    to: '/evv-history',
    icon: HistoryIcon,
    roles: [Roles.Admin],
  },
  {
    title: 'Bulk Mark Unbillable',
    to: '/admin/bulk-unbillable',
    icon: ChangeIcon,
    roles: [Roles.Admin],
  },
  {
    title: 'Reassign TP',
    to: '/admin/reassign-treatment-plan',
    icon: ReassignIcon,
    roles: [Roles.Admin, Roles.OnboardingCoordinator],
  },
  {
    title: 'Send Assessment Survey',
    to: '/admin/assessment-survey',
    icon: SendIcon,
    roles: [Roles.Admin],
  },
];

const nestedSupervisionNoteItems = [
  { title: 'Direct (53)', to: '/new-session', icon: DirectSession, roles: [Roles.Bcba] },
  { title: 'Client (55)', to: '/client-supervisions/new', icon: ClientIcon, roles: [Roles.Bcba] },
  { title: 'BT', to: '/bt-supervisions/new', icon: BtIcon, roles: [Roles.Bcba] },
  { title: 'Parent (56)', to: '/parent-supervisions/new', icon: ParentsIcon, roles: [Roles.Bcba] },
  { title: 'Group (57)', to: '/group-parent-training/new', icon: GroupsIcon, roles: [Roles.Bcba] },
  {
    title: 'Assessment (51 / 52)',
    to: '/assessment-note/new',
    icon: ClientAssessmentIcon,
    roles: [Roles.Bcba],
  },
];

const nestedHistoricalDocsItems = [
  {
    title: 'List',
    to: '/historical-documents',
    icon: ListIcon,
    roles: [
      Roles.Admin,
      Roles.Bcba,
      Roles.AssistantAssessor,
      Roles.ClientSuccessManager,
      Roles.Biller,
      Roles.InvoiceCoordinator,
      Roles.QaSpecialist,
      Roles.LimitedAccessGuardian,
    ],
  },
  {
    title: 'Calendar',
    to: '/historical-documents-calendar-view',
    icon: CalenderIcon,
    roles: [Roles.Bcba],
  },
];

const nestedReportItems = [
  {
    title: 'Client progress',
    to: '/data-dashboard',
    icon: ChartIcon,
    roles: [Roles.Bcba, Roles.ClientSuccessManager, Roles.Admin],
  },
  {
    title: 'Supervision Metrics',
    to: '/supervision-metrics',
    icon: SupervisionMetricsIcon,
    roles: [Roles.Bcba, Roles.ClientSuccessManager, Roles.Admin],
  },
  {
    title: 'TP activation',
    to: '/tp-activation',
    icon: ListIcon,
    roles: [Roles.Bcba, Roles.Admin],
  },
];

const getNavItems = (t: TFunction<'sideBar'>) => [
  {
    title: t('home'),
    to: '/dashboard',
    icon: { url: HomeIcon, width: 23, height: 24 },
    roles: [
      Roles.Prbt,
      Roles.Admin,
      Roles.Bcba,
      Roles.AssistantAssessor,
      Roles.ClientSuccessManager,
      Roles.Biller,
      Roles.Guardian,
      Roles.Vbt,
      Roles.LimitedAccessGuardian,
      Roles.QaSpecialist,
    ],
  },
  {
    title: 'QA Dashboard',
    to: '/qa-dashboard',
    icon: {
      url: EditSessionIcon,
      width: 24,
      height: 25,
    },
    roles: [Roles.QaSpecialist, Roles.Admin, Roles.ClientSuccessManager],
  },
  {
    title: 'Claims', // this should be biller dashboard
    to: '/billings/claims',
    icon: {
      url: BillIcon,
      width: 24,
      height: 24,
    },
    roles: [Roles.Biller],
  },
  {
    title: 'New session',
    to: '/new-session',
    icon: { url: NewIcon, width: 20, height: 24 },
    roles: [Roles.Prbt, Roles.Vbt],
  },
  {
    title: 'Create invoice',
    to: '/billings/invoice-builder',
    icon: { url: CreateInvoiceIcon, width: 20, height: 24 },
    roles: [Roles.InvoiceCoordinator],
  },
  {
    title: 'New assessment',
    to: '/assessment-note/new',
    icon: { url: NewIcon, width: 20, height: 24 },
    roles: [Roles.AssistantAssessor],
  },
  {
    title: 'Admin Actions',
    icon: { url: TaskIcon, width: 20, height: 24 },
    roles: [Roles.Admin, Roles.OnboardingCoordinator, Roles.Biller, Roles.ClientSuccessManager],
    nestedItems: nestedAdminActionsItems,
  },
  {
    title: 'Documents',
    icon: { url: NewIcon, width: 20, height: 24 },
    roles: [Roles.Bcba],
    nestedItems: nestedSupervisionNoteItems,
  },
  {
    title: 'Historical Docs',
    icon: { url: BackgroundIcon, width: 20, height: 24 },
    roles: [Roles.Bcba],
    nestedItems: nestedHistoricalDocsItems,
    sx: { mt: 1.5 },
  },
  {
    title: 'Historical Documents',
    to: '/historical-documents',
    icon: { url: BackgroundIcon, width: 20, height: 24 },
    roles: [
      Roles.Admin,
      Roles.AssistantAssessor,
      Roles.ClientSuccessManager,
      Roles.Biller,
      Roles.Guardian,
      Roles.InvoiceCoordinator,
      Roles.QaSpecialist,
      Roles.LimitedAccessGuardian,
    ],
    hideForPreClinicalGuardian: true,
  },
  {
    title: t('historicalNotes'),
    to: '/historical-notes',
    icon: { url: HistoryIcon, width: 24, height: 24 },
    roles: [Roles.Prbt, Roles.Admin, Roles.ClientSuccessManager, Roles.Vbt],
  },
  {
    title: t('invoices'),
    to: '/billings/invoice-list',
    icon: { url: PaymentIcon, width: 24, height: 24 },
    roles: [Roles.Guardian, Roles.Prbt, Roles.LimitedAccessGuardian],
    hideForPreClinicalGuardian: true,
  },
  {
    title: 'Timesheet',
    to: '/timesheet',
    icon: { url: AlarmIcon, width: 25, height: 24 },
    roles: [
      Roles.Admin,
      Roles.AssistantAssessor,
      Roles.Bcba,
      Roles.ClientSuccessManager,
      Roles.Prbt,
      Roles.Vbt,
    ],
  },
  {
    title: 'Treatment Plan',
    to: '/treatment-plan',
    icon: { url: HeartIcon, width: 24, height: 24 },
    roles: [
      Roles.Bcba,
      Roles.AssistantAssessor,
      Roles.Admin,
      Roles.ClientSuccessManager,
      Roles.QaSpecialist,
      Roles.Biller,
      Roles.InvoiceCoordinator,
      Roles.Vbt,
    ],
    iconStyle: { marginLeft: '4px', marginTop: '4px' },
  },
  {
    title: 'Reports',
    icon: { url: ReportsIcon, width: 20, height: 24 },
    roles: [Roles.Bcba, Roles.ClientSuccessManager, Roles.Admin],
    nestedItems: nestedReportItems,
    sx: { mb: 2.5 },
  },
  {
    title: 'Data Dashboard',
    to: '/data-dashboard',
    icon: { url: ChartIcon, width: 24, height: 24 },
    roles: [
      Roles.AssistantAssessor,
      Roles.QaSpecialist,
      Roles.Vbt,
      Roles.Prbt,
      Roles.LimitedAccessGuardian,
    ],
  },
];

const NavItems = ({ isCollapsed }: { isCollapsed: boolean }) => {
  const { t } = useTranslation('sideBar');
  const { user } = useSelector((state: RootState) => state.auth);
  const { hideClinicalFeatures } = usePreClinicalGuardianGating();

  return (
    <Box
      sx={{
        flexGrow: 1,
        mt: 7,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '92%',
        padding: 1,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '0.8em',
        },
      }}
    >
      <Box>
        {getNavItems(t)
          .filter(
            (item) =>
              areUserRolesIn(user?.roles, item.roles) &&
              !(item.hideForPreClinicalGuardian && hideClinicalFeatures),
          )
          .map((item) =>
            item.nestedItems ? (
              <NestedNavItem
                key={item.title}
                title={item.title}
                icon={item.icon}
                iconStyle={item.iconStyle}
                nestedItems={item.nestedItems}
                isCollapsed={isCollapsed}
                sx={item.sx}
              />
            ) : (
              <NavItem
                key={item.title}
                title={item.title}
                to={item.to}
                icon={item.icon}
                iconStyle={item.iconStyle}
                isCollapsed={isCollapsed}
              />
            ),
          )}
        <FHTooltip
          title={t('knowledgeBase')}
          tooltipBorderRadius="8px"
          tooltipBoxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5em' }}>
            <Link sx={itemStyle} href={'https://kb.fortahealth.com/'} target="_blank">
              <img
                src={KnowledgeBaseIcon}
                alt="nav-icon"
                width={isCollapsed ? '30' : '26'}
                height={isCollapsed ? '30' : '26'}
                style={{
                  transition: 'all 0.3s ease-in-out',
                  marginLeft: isCollapsed ? '7px' : '-8px',
                }}
              />
              {!isCollapsed && (
                <Typography
                  style={{
                    opacity: 1,
                    color: 'white',
                    whiteSpace: 'nowrap',
                    fontSize: '15px',
                  }}
                >
                  {t('knowledgeBase')}
                </Typography>
              )}
            </Link>
          </Box>
        </FHTooltip>
      </Box>
    </Box>
  );
};

function SideBar() {
  const location = useLocation();
  const { sidebarWidth, setSidebarWidth, isSidebarToggled, setSidebarToggled } = useSidebar();

  const [menuOpen, setMenuOpen] = useState(false); //Mobile menu state
  const [isCollapsed, setIsCollapsed] = useState(true); // Initially collapsed
  const [isHovered, setIsHovered] = useState(false); // Track hover state

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    setSidebarToggled(!isSidebarToggled);
  };

  const toggleDrawer = (isOpen: boolean) => {
    setMenuOpen(isOpen);
  };

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const width = isCollapsed && !isHovered ? 100 : SIDEBAR_WIDTH;
    setSidebarWidth(width);
  }, [isCollapsed, isHovered, setSidebarWidth]);

  return (
    <>
      <Box
        data-testid="appSideNav"
        sx={{
          width: sidebarWidth,
          height: '100vh',
          backgroundColor: FORTA_BLUE.primary,
          py: 4,
          position: 'fixed',
          zIndex: 10,
          display: { xs: 'none', sm: 'none', md: 'block' },
          transition: 'all 0.3s ease-in-out',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <IconButton
          sx={{
            width: 24,
            height: 24,
            position: 'absolute',
            top: '2.7em',
            right: '-0.5em',
            zIndex: 100,
            backgroundColor: FORTA_WHITE,
            color: FORTA_WHITE,
            borderRadius: 99,
            border: `1px solid ${FORTA_BLUE.primary}`,
            '&:hover': {
              backgroundColor: FORTA_WHITE,
              color: FORTA_WHITE,
            },
          }}
          onClick={toggleSidebar}
        >
          {isCollapsed && !isSidebarToggled ? (
            <FHTooltip title="Keep expanded" placement="right" sx={{ borderRadius: '10px' }} arrow>
              <ChevronRight sx={{ color: FORTA_BLUE.primary }} />
            </FHTooltip>
          ) : (
            <FHTooltip title="Collapse" placement="right" sx={{ borderRadius: '10px' }} arrow>
              <ChevronLeft sx={{ color: FORTA_BLUE.primary }} />
            </FHTooltip>
          )}
        </IconButton>
        <Box sx={{ height: '5%', py: 2 }}>
          <NavLink to="/">
            <Box
              sx={{
                display: 'flex',
                direction: 'row-reverse',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '1.5em',
                gap: '0.5em',
                padding: '10px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '0.8em',
                }}
              >
                {!isCollapsed || isHovered ? (
                  <LogoIcon color={FORTA_WHITE} width={36} height={36} />
                ) : (
                  <img src={LogoWithText} alt="forta-logo" />
                )}
                {(!isCollapsed || isHovered) && (
                  <Typography style={{ color: FORTA_WHITE, fontSize: '28px', fontWeight: 700 }}>
                    Forta
                  </Typography>
                )}
              </Box>
            </Box>
          </NavLink>
        </Box>
        <NavItems isCollapsed={isCollapsed && !isHovered} />
      </Box>
      <Box>
        <IconButton
          sx={{
            display: { sm: 'visible', md: 'none' },
            position: 'fixed',
            top: '1.2em',
            left: '0.8em',
            zIndex: 100,
            backgroundColor: FORTA_BLUE.primary,
            color: FORTA_WHITE,
            borderRadius: '10px',
          }}
          onClick={() => toggleDrawer(true)}
        >
          <MenuIcon />
        </IconButton>
        <Drawer
          anchor="left"
          open={menuOpen}
          onClose={() => toggleDrawer(false)}
          sx={{
            display: { xs: 'visible', sm: 'visible', md: 'none' },
            '& .MuiModal-backdrop': {
              backdropFilter: 'blur(2px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          }}
        >
          <IconButton
            style={{ position: 'fixed', top: '10px', right: '10px' }}
            onClick={() => toggleDrawer(false)}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              minWidth: SIDEBAR_WIDTH,
              height: '100vh',
              backgroundColor: FORTA_BLUE.primary,
              py: 4,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <NavLink to="/">
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: '1.5em',
                  gap: '0.5em',
                }}
              >
                <LogoIcon color={FORTA_WHITE} width={32} height={32} />
                <Typography style={{ color: FORTA_WHITE, fontSize: '28px', fontWeight: 700 }}>
                  Forta
                </Typography>
              </Box>
            </NavLink>
            <NavItems isCollapsed={false} />
          </Box>
        </Drawer>
      </Box>
    </>
  );
}

export default SideBar;
